import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, CubeIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api/config';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { lazyWithReload } from '../../../../utils/lazyWithReload';
import {
    fetchCloudFileDownloadUrl,
    type PatientCloudFile
} from '../../../../services/patientFiles';
import { uploadSparkScans } from '../../../../services/sparkSubmission';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { SIGNED_URL_GC_TIME_MS } from '../../../../providers/queryClient';
import {
    STL_PREVIEW_MAX_BYTES,
    archOf,
    formatUploadDate,
    useCloudFiles,
    type RegisterFooter,
    type RegisterBusy
} from './spark.shared';

// Shared 3D STL viewer (three.js), lazy-loaded only when a scan is opened.
// lazyWithReload: a deploy invalidates old hashed chunk URLs in open tabs.
const STLViewer = lazyWithReload(() =>
    import('@odo/shared-ui/components/stl/STLViewer').then((m) => ({ default: m.default }))
);

// Static one-frame STL thumbnail, lazy so three.js only loads on the scans step.
const StlThumbnail = lazyWithReload(() => import('./StlThumbnail'));

//  Step 3 - select STL scans, preview them in 3D, upload & finish

export function UploadScansStep({
    patientId,
    advancing,
    onFinish,
    registerFooter,
    registerBusy,
    selected,
    setSelected,
    caseId
}: {
    patientId: string;
    advancing: boolean;
    onFinish: () => Promise<boolean>;
    registerFooter: RegisterFooter;
    registerBusy: RegisterBusy;
    selected: Record<string, boolean>;
    setSelected: Dispatch<SetStateAction<Record<string, boolean>>>;
    /** Optional Spark case to upload to (e.g. a refinement case). Defaults to the
     *  patient's stored manufacturer case when omitted. */
    caseId?: number;
}) {
    const { showToast } = useToast();
    const { data: files, isLoading } = useCloudFiles(patientId, 'stl');

    const [busy, setBusy] = useState(false);
    const [viewing, setViewing] = useState<PatientCloudFile | null>(null);

    const selections = useMemo(
        () =>
            Object.entries(selected)
                .filter(([, on]) => on)
                .map(([patientFileId]) => ({ patientFileId })),
        [selected]
    );

    const handleUploadAndFinish = useCallback(async () => {
        if (selections.length === 0) return;
        setBusy(true);
        try {
            const res = await uploadSparkScans(patientId, selections, caseId);
            // Hold the success toast until the case has actually advanced, so it lands
            // right before the redirect rather than ahead of the advance call.
            const advanced = await onFinish();
            if (advanced) {
                showToast(`Uploaded ${res.uploaded} scan(s) to Spark.`, 'success');
            } else {
                setBusy(false);
            }
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to upload scans.'), 'error');
            setBusy(false);
        }
    }, [patientId, selections, caseId, onFinish, showToast]);

    const primaryLabel =
        busy || advancing
            ? 'Finishing…'
            : `Upload ${selections.length} scan${selections.length === 1 ? '' : 's'} & finish`;

    useEffect(() => {
        registerFooter({
            label: primaryLabel,
            onClick: () => void handleUploadAndFinish(),
            disabled: busy || advancing || selections.length === 0
        });
        return () => registerFooter(null);
    }, [registerFooter, primaryLabel, handleUploadAndFinish, busy, advancing, selections.length]);

    // Lock wizard navigation while the upload (and follow-up advance) is in flight.
    useEffect(() => {
        registerBusy(busy || advancing);
        return () => registerBusy(false);
    }, [registerBusy, busy, advancing]);

    return (
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-4">
            <p className="text-sm text-gray-600 text-center">
                Select the STL scans to send to Spark.
            </p>
            {isLoading ? (
                <div className="text-sm text-gray-400 py-6 text-center">Loading scans…</div>
            ) : !files || files.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">
                    No STL scans found on this patient.
                </div>
            ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                    {files.map((f) => {
                        const on = selected[f.id] ?? false;
                        const arch = archOf(f.file.originalFilename);
                        const sizeBytes = Number(f.file.sizeBytes ?? 0);
                        // Don't download/render STLs above the preview cap; they can still be
                        // selected and uploaded (that download happens server-side).
                        const tooLargeToPreview = sizeBytes > STL_PREVIEW_MAX_BYTES;
                        return (
                            <div
                                key={f.id}
                                onClick={() => setSelected((s) => ({ ...s, [f.id]: !on }))}
                                className={`rounded-2xl border overflow-hidden bg-white cursor-pointer transition-colors ${
                                    on
                                        ? 'border-gray-800 ring-1 ring-gray-800'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="relative aspect-[16/10] bg-gradient-to-b from-gray-100 to-gray-200">
                                    {tooLargeToPreview ? (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center">
                                            <CubeIcon
                                                className="w-12 h-12 text-gray-400"
                                                strokeWidth={1}
                                            />
                                            <span className="text-[10px] font-medium text-gray-400">
                                                Too large to preview
                                            </span>
                                        </div>
                                    ) : (
                                        <ScanThumb patientId={patientId} file={f} />
                                    )}
                                    {arch.short && (
                                        <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md bg-white/90 text-[10px] font-bold tracking-wide text-gray-600">
                                            {arch.short}
                                        </span>
                                    )}
                                    <span
                                        className={`absolute top-2.5 right-2.5 flex items-center justify-center w-6 h-6 rounded-full ${
                                            on
                                                ? 'bg-gray-900 text-white'
                                                : 'border-2 border-gray-300 bg-white/80'
                                        }`}
                                    >
                                        {on && <CheckIcon className="w-3.5 h-3.5" />}
                                    </span>
                                </div>
                                <div className="p-3.5">
                                    <div className="text-sm font-bold text-gray-900 truncate">
                                        {f.file.originalFilename}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">{arch.label}</div>
                                    <div className="text-xs text-gray-400">
                                        Uploaded {formatUploadDate(f.file.createdAt)}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setViewing(f);
                                        }}
                                        className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <EyeIcon className="w-4 h-4" /> View 3D
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {files && files.length > 0 && (
                <p className="text-xs text-gray-400 text-center">
                    Only selected scans are uploaded to Spark. At least one scan is required to
                    finish.
                </p>
            )}

            {viewing && (
                <Scan3DViewerModal
                    patientId={patientId}
                    file={viewing}
                    onClose={() => setViewing(null)}
                />
            )}
        </div>
    );
}

//  Static STL thumbnail for a scan card - resolves a signed URL (shared with
//  the View 3D modal's cache) then renders one frame via the lazy thumbnail.

function ScanThumb({ patientId, file }: { patientId: string; file: PatientCloudFile }) {
    const {
        data: url,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['cloud-file-download-url', patientId, file.id],
        queryFn: () => fetchCloudFileDownloadUrl(patientId, file.id),
        staleTime: 5 * 60 * 1000,
        gcTime: SIGNED_URL_GC_TIME_MS
    });

    if (isError) {
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <CubeIcon className="w-14 h-14 text-gray-400" strokeWidth={1} />
            </div>
        );
    }
    if (isLoading || !url) {
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
            </div>
        );
    }
    return (
        <Suspense
            fallback={
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                </div>
            }
        >
            <StlThumbnail stlUrl={url} />
        </Suspense>
    );
}

//  3D STL preview modal - fetches a signed URL and renders the shared STLViewer.

function Scan3DViewerModal({
    patientId,
    file,
    onClose
}: {
    patientId: string;
    file: PatientCloudFile;
    onClose: () => void;
}) {
    const arch = archOf(file.file.originalFilename);
    const {
        data: url,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['cloud-file-download-url', patientId, file.id],
        queryFn: () => fetchCloudFileDownloadUrl(patientId, file.id),
        staleTime: 5 * 60 * 1000,
        gcTime: SIGNED_URL_GC_TIME_MS
    });

    useEscapeKey(onClose, true);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-6">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-[720px] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center gap-4 px-5 py-4 border-b border-gray-100">
                    <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">
                            {file.file.originalFilename}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{arch.label} · STL mesh</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-800"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
                <div className="relative aspect-[16/10] bg-gray-50">
                    {isError ? (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
                            Couldn't load the 3D preview.
                        </div>
                    ) : isLoading || !url ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                        </div>
                    ) : (
                        <Suspense
                            fallback={
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                </div>
                            }
                        >
                            <STLViewer
                                stlUrl={url}
                                apiBaseUrl={api.defaults.baseURL ?? ''}
                                className="h-full"
                            />
                        </Suspense>
                    )}
                </div>
                <div className="flex justify-end items-center px-5 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-gray-900 text-white text-sm font-semibold px-5 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
